import { stringify } from "qs";

import { API_ENDPOINT } from "../../config/api";
import { fetchWithAuth } from "../api/fetch-with-auth";
import { downloadResponseFile } from "../utils/download-response-file";
import { getSearchParamsArrayFormat } from "../utils/get-search-params-array-format";

import { readErrorResponse } from "./read-error-response";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TAbstractFilter = Record<string, any>;

type TExtendedRequestInit<T> = RequestInit &
  Partial<{
    searchParams: Record<string, unknown> & { filter?: T };
    baseUrl: string;
    raw: boolean;
  }>;

function buildUrlWithSearch(
  url: string,
  searchParamsObj: TAbstractFilter = {},
  searchParams: URLSearchParams = new URLSearchParams()
): string {
  const mergedParams = Array.from(searchParams.entries()).reduce(
    (acc, [key, value]) => {
      if (key in acc) {
        if (!Array.isArray(acc[key])) {
          acc[key] = [acc[key]];
        }
        acc[key].push(value);
      } else {
        acc[key] = value;
      }
      return acc;
    },
    { ...searchParamsObj }
  );
  const arrayFormat = getSearchParamsArrayFormat(url);
  return `${url}?${stringify(mergedParams, { arrayFormat })}`;
}

export abstract class BaseService<TDto, T extends TAbstractFilter> {
  public readonly ROOT_URL: string = API_ENDPOINT;

  abstract list(filter: T, include: unknown): Promise<TDto[]>;
  abstract get(id: string): Promise<TDto>;

  protected async request<TResponse = TDto, R = T>(
    path: string,
    init: TExtendedRequestInit<R> = {}
  ): Promise<TResponse> {
    const { raw = false, baseUrl = this.ROOT_URL, searchParams, ...nativeInit } = init;
    const {
      origin,
      pathname,
      searchParams: paramsFromUrl,
    } = new URL([baseUrl, path].join("/"));
    const url = buildUrlWithSearch(origin + pathname, searchParams, paramsFromUrl);
    const response = await fetchWithAuth(url, nativeInit);

    if (raw) {
      return response as unknown as TResponse;
    }
    if (!response.ok) {
      const error = await readErrorResponse(response);
      throw error;
    }
    return response.json() as Promise<TResponse>;
  }

  downloadFile(url: string): void {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.setAttribute("download", "");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadFileAsync(
    url: string,
    options: Partial<{ filename: string; searchParams: Record<string, unknown> }> = {}
  ): Promise<void> {
    const { filename = "download.txt", ...rest } = options;
    return this.request<Response>(url, { raw: true, ...rest }).then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText || String(response.status));
      }

      return downloadResponseFile(response, filename);
    });
  }
}
